import { API_BASE_URL } from "../../../config";
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import CustomerLink from '../../Components/CustomerLink';
import { codeYear } from './tankSearch';
import useFetchUserSeller from '../../Utils/useFetchUserSeller';
import { assignCustomerToRoute } from '../../Utils/assignCustomerRoute';

// Shared tank create / move / return / history dialogs (vector 2026-10-06), used by both
// TankManagement.js and CustomerDetail.js. Every action surfaces the server's own error text —
// nothing here reports success unless the response was ok.

export const STATUS_LABELS = {
    ACTIVE: 'ใช้งาน', LOST: 'หาย', SEARCHING: 'กำลังตามหา', NO_DATA: 'ไม่มีข้อมูล',
    RETURNED: 'คืนแล้ว', LENT: 'ยืม', DONATED: 'ยกให้', DAMAGED: 'ชำรุด',
};

const MOVEMENT_LABELS = { CREATE: 'สร้าง', MOVE: 'ย้าย', RETURN: 'ถอนคืนโรงงาน', STATUS_CHANGE: 'เปลี่ยนสถานะ' };

const CODE_GENERATION_OPTIONS = [
    { value: 'OLD', label: 'รุ่นเก่า (รหัสสั้น)' },
    { value: 'NEW', label: 'รุ่นใหม่ (รหัสยาว)' },
];

// uid -> name for the "ผู้ทำ" column. Reads the IAM user list once per page load (same /v4/users the
// user-management page uses). System ids are never shown inline (vector 2026-10-06): a viewer without
// permission for that list, a failed fetch, or an unknown uid all show "ไม่ทราบชื่อ" — the uid is
// only revealed when the viewer clicks the name (see RevealCode).
let userDirectoryPromise = null;
export const loadUserDirectory = () => {
    if (!userDirectoryPromise) {
        const user = localStorage.getItem('user');
        const token = user ? JSON.parse(user)?.token : null;
        userDirectoryPromise = fetch(`${API_BASE_URL}/auth/api/main/v4/users`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => (res.ok ? res.json() : []))
            .then((list) => {
                const map = {};
                (Array.isArray(list) ? list : []).forEach((u) => {
                    if (u?.uid) map[u.uid] = u.nameuser || u.username || '';
                });
                return map;
            })
            .catch(() => ({}));
    }
    return userDirectoryPromise;
};

export const formatActor = (uid, directory) => {
    if (!uid) return '-';
    return directory?.[uid] || UNKNOWN_NAME;
};

export const UNKNOWN_NAME = 'ไม่ทราบชื่อ';

// Shows a human name; the system id behind it (uid / customer code) appears only after a click.
export const RevealCode = ({ name, code, codeLabel = 'รหัส' }) => {
    const [open, setOpen] = useState(false);
    if (!code) return <>{name || '-'}</>;
    return (
        <span>
            <span
                role="button"
                tabIndex={0}
                title="กดเพื่อดูรหัส"
                onClick={() => setOpen((o) => !o)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
                style={{ cursor: 'pointer', borderBottom: '1px dotted #6B6B6B' }}
            >
                {name || UNKNOWN_NAME}
            </span>
            {open && <span style={{ color: '#6B6B6B', fontSize: '0.8rem', marginLeft: 6 }}>{codeLabel}: {code}</span>}
        </span>
    );
};

export const ActorName = ({ uid, directory }) => (
    uid ? <RevealCode name={formatActor(uid, directory)} code={uid} codeLabel="รหัสผู้ใช้" /> : <>-</>
);

export const isLocked = (tank) => tank?.status === 'DONATED';

// Returns { ok, data, status, message } — never throws, so callers can't forget the failure branch.
export const tankApi = async (path, { method = 'GET', body } = {}) => {
    const user = localStorage.getItem('user');
    const token = user ? JSON.parse(user)?.token : null;
    try {
        const response = await fetch(`${API_BASE_URL}/customer/api/tanks${path}`, {
            method,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: body ? JSON.stringify(body) : undefined,
        });
        const text = await response.text();
        if (!response.ok) {
            return { ok: false, status: response.status, message: text || `ผิดพลาด (${response.status})` };
        }
        let data = null;
        try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
        return { ok: true, status: response.status, data };
    } catch (e) {
        return { ok: false, status: 0, message: 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้' };
    }
};

const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', background: '#fff', color: '#212529' };
const labelStyle = { color: '#6B6B6B', fontSize: '0.85rem', marginBottom: 4 };

const Modal = ({ title, onClose, children, width = 440 }) => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ background: '#fff', borderRadius: '10px', padding: '24px', width, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div style={{ fontWeight: 600, fontSize: '16px' }}>{title}</div>
                <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6B6B6B' }}>×</button>
            </div>
            {children}
        </div>
    </div>
);

const Footer = ({ onCancel, onSubmit, busy, submitLabel, cancelLabel = 'ยกเลิก' }) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
        <button className="btc-btn btn-sm" style={{ backgroundColor: '#6B6B6B' }} onClick={onCancel} disabled={busy}>{cancelLabel}</button>
        {onSubmit && (
            <button className="btc-btn btn-sm" style={{ backgroundColor: '#E60012' }} onClick={onSubmit} disabled={busy}>
                {busy ? 'กำลังบันทึก...' : submitLabel}
            </button>
        )}
    </div>
);

// REGULAR customers only — tanks can't link to any other type. Loaded once per mount. Labels are the
// store name; when two stores share a name the label gets the seller route (สาย) appended, then the
// owner name, then an ordinal — never the system code. Typing a code still matches (react-select's
// default filter also searches option.value) but the code is not displayed.
export const useCustomerDirectory = () => {
    const [state, setState] = useState({ options: [], names: {}, loading: true, error: '' });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const user = localStorage.getItem('user');
            const token = user ? JSON.parse(user)?.token : null;
            const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
            try {
                const response = await fetch(`${API_BASE_URL}/customer/api/customers/by-category`, {
                    method: 'POST', headers, body: JSON.stringify({ type: 'REGULAR' }),
                });
                if (!response.ok) throw new Error(String(response.status));
                const data = await response.json();
                const customers = (data?.regular || []).map((c) => ({
                    uid: c.customerUid,
                    name: c.storeName || UNKNOWN_NAME,
                    owner: c.customerName && c.customerName !== c.storeName ? c.customerName : '',
                }));
                const count = {};
                customers.forEach((c) => { count[c.name] = (count[c.name] || 0) + 1; });
                const dupes = customers.filter((c) => count[c.name] > 1);

                let routesByUid = {};
                if (dupes.length > 0) routesByUid = await loadRoutesFor(new Set(dupes.map((c) => c.uid)), headers);

                const labels = {};
                const used = {};
                customers.forEach((c) => {
                    let label = c.name;
                    if (count[c.name] > 1) {
                        const extra = (routesByUid[c.uid] || []).join(', ') || c.owner;
                        if (extra) label = `${c.name} · ${extra}`;
                    }
                    used[label] = (used[label] || 0) + 1;
                    labels[c.uid] = used[label] > 1 ? `${label} (${used[label]})` : label;
                });
                if (cancelled) return;
                setState({
                    options: customers.map((c) => ({ value: c.uid, label: labels[c.uid] })),
                    names: labels, loading: false, error: '',
                });
            } catch (e) {
                if (!cancelled) setState({ options: [], names: {}, loading: false, error: 'โหลดรายชื่อลูกค้าไม่สำเร็จ' });
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Display name for a customer code; falls back to the name the server sent (or "ไม่ทราบชื่อ").
    const nameOf = (code, fallback) => state.names[code] || fallback || UNKNOWN_NAME;
    return { ...state, nameOf };
};

// seller route (สาย) names per customer, only for the customers that need disambiguating. Best
// effort: any failure just means the label falls back to the owner name.
const loadRoutesFor = async (uids, headers) => {
    try {
        const res = await fetch(`${API_BASE_URL}/customer/api/customer-buy-rutine/all`, { headers });
        if (!res.ok) return {};
        const rows = (await res.json()).filter((r) => uids.has(r.customerId) && r.sellerId);
        const sellerIds = [...new Set(rows.map((r) => r.sellerId))];
        const nameBySeller = {};
        if (sellerIds.length > 0) {
            const namesRes = await fetch(`${API_BASE_URL}/customer/api/customers/seller-names`, {
                method: 'POST', headers, body: JSON.stringify(sellerIds),
            });
            if (namesRes.ok) (await namesRes.json()).forEach((n) => { if (n.nameuser) nameBySeller[n.Seller_ID] = n.nameuser; });
        }
        const out = {};
        rows.forEach((r) => {
            const n = nameBySeller[r.sellerId];
            if (n && !(out[r.customerId] || []).includes(n)) (out[r.customerId] = out[r.customerId] || []).push(n);
        });
        return out;
    } catch (e) {
        return {};
    }
};

export const CustomerPicker = (props) => {
    const own = useCustomerDirectory();
    return <CustomerSelect {...props} directory={props.directory || own} />;
};

const CustomerSelect = ({ value, onChange, placeholder = 'พิมพ์ชื่อร้านเพื่อค้นหา...', directory }) => {
    const { options, loading, error: loadError } = directory;

    return (
        <>
            <Select
                options={options}
                value={options.find((o) => o.value === value) || null}
                onChange={(opt) => onChange(opt ? opt.value : '')}
                isLoading={loading}
                isClearable
                isSearchable
                placeholder={placeholder}
                noOptionsMessage={() => 'ไม่พบร้านที่ค้นหา'}
                className="btc-selectBox-container"
                classNamePrefix="btc-selectBox"
            />
            {loadError && <p className="error" style={{ marginTop: 6 }}>{loadError}</p>}
        </>
    );
};

const today = () => new Date().toISOString().slice(0, 10);

export const CreateTankDialog = ({ onClose, onDone, presetCustomer }) => {
    const [form, setForm] = useState({
        tankId: '', codeGeneration: 'NEW', brand: '', size: '', route: '',
        customerCode: presetCustomer?.code || '', datePlaced: today(), note: '',
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    // สายของลูกค้า (vector 2026-10-14): picking a route here ADDS the customer to that route in
    // customer_buy_rutine (same table as the customer page); it never removes the customer's other
    // routes. The free-text tank label `route` above is kept as-is (the tank list filter reads it).
    const sellers = useFetchUserSeller();
    const routeList = Array.isArray(sellers) ? sellers : [];
    const [addRouteUid, setAddRouteUid] = useState('');
    const [currentRoutes, setCurrentRoutes] = useState([]);
    const [tankCreated, setTankCreated] = useState(false);

    useEffect(() => {
        setAddRouteUid('');
        setCurrentRoutes([]);
        if (!form.customerCode) return undefined;
        let cancelled = false;
        (async () => {
            const user = localStorage.getItem('user');
            const token = user ? JSON.parse(user)?.token : null;
            try {
                const res = await fetch(`${API_BASE_URL}/customer/api/customer-buy-rutine/all`, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) return;
                const rows = await res.json();
                if (cancelled || !Array.isArray(rows)) return;
                setCurrentRoutes([...new Set(rows.filter((r) => r.customerId === form.customerCode && r.sellerId).map((r) => r.sellerId))]);
            } catch (e) { /* best effort: the list only informs the user */ }
        })();
        return () => { cancelled = true; };
    }, [form.customerCode]);

    const routeName = (uid) => {
        const r = routeList.find((x) => x.uid === uid);
        return r ? (r.nameuser || r.username || UNKNOWN_NAME) : UNKNOWN_NAME;
    };

    const submit = async () => {
        if (!form.tankId.trim()) { setError('ต้องกรอกรหัสถัง'); return; }
        if (!form.customerCode && !form.note.trim()) { setError('ถ้าไม่เลือกลูกค้า ต้องกรอกหมายเหตุอธิบายเหตุผล'); return; }
        setBusy(true);
        setError('');
        if (tankCreated) {
            // tank already saved on an earlier click; only the route step is being retried
            const retry = await applyRoute();
            setBusy(false);
            if (retry) onDone();
            return;
        }
        const res = await tankApi('', {
            method: 'POST',
            body: {
                tankId: form.tankId.trim(),
                codeGeneration: form.codeGeneration.trim() || null,
                brand: form.brand || null,
                size: form.size || null,
                route: form.route || null,
                customerCode: form.customerCode || null,
                datePlaced: form.datePlaced || null,
                note: form.note || null,
                status: form.customerCode ? 'ACTIVE' : 'RETURNED',
            },
        });
        if (!res.ok) { setBusy(false); setError(res.message); return; }
        setTankCreated(true);
        const routeOk = await applyRoute();
        setBusy(false);
        if (routeOk) onDone();
    };

    // true when there is nothing to do or the route was set; false (with the error shown) otherwise
    async function applyRoute() {
        if (!form.customerCode || !addRouteUid || currentRoutes.includes(addRouteUid)) return true;
        try {
            const user = localStorage.getItem('user');
            const token = user ? JSON.parse(user)?.token : null;
            await assignCustomerToRoute(token, form.customerCode, addRouteUid);
            return true;
        } catch (e) {
            setError(`สร้างถังแล้ว แต่ตั้งสายของลูกค้าไม่สำเร็จ (${e.message}) — กด "สร้างถัง" อีกครั้งเพื่อลองตั้งสายใหม่ หรือกดยกเลิกแล้วไปตั้งที่หน้าลูกค้า`);
            return false;
        }
    }

    return (
        <Modal title={presetCustomer ? `เพิ่มถังให้ ${presetCustomer.name || 'ร้านนี้'}` : 'สร้างถัง'} onClose={onClose}>
            <div style={{ marginBottom: 12 }}>
                <div style={labelStyle}>รหัสถัง *</div>
                <input type="text" aria-label="รหัสถัง" value={form.tankId} onChange={set('tankId')} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
                <div style={labelStyle}>รุ่นรหัส (เลือกจากรายการหรือพิมพ์เอง)</div>
                <input type="text" aria-label="รุ่นรหัส" list="tank-code-generations" value={form.codeGeneration} onChange={set('codeGeneration')} style={inputStyle} />
                <datalist id="tank-code-generations">
                    {CODE_GENERATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </datalist>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                    <div style={labelStyle}>ยี่ห้อ</div>
                    <input type="text" aria-label="ยี่ห้อ" value={form.brand} onChange={set('brand')} style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                    <div style={labelStyle}>ขนาด</div>
                    <input type="text" aria-label="ขนาด" value={form.size} onChange={set('size')} style={inputStyle} />
                </div>
            </div>
            <div style={{ marginBottom: 12 }}>
                <div style={labelStyle}>ป้ายสายบนถัง (ข้อความ)</div>
                <input type="text" aria-label="ป้ายสายบนถัง" value={form.route} onChange={set('route')} style={inputStyle} />
            </div>
            {!presetCustomer && (
                <div style={{ marginBottom: 12 }}>
                    <div style={labelStyle}>ลูกค้า (ไม่บังคับ)</div>
                    <CustomerPicker value={form.customerCode} onChange={(v) => setForm((f) => ({ ...f, customerCode: v }))} />
                </div>
            )}
            {form.customerCode && (
                <div style={{ marginBottom: 12 }}>
                    <div style={labelStyle}>ตั้งสายของลูกค้า (เพิ่มลูกค้าเข้าสายนี้ ไม่ลบสายอื่นที่มีอยู่)</div>
                    <div data-testid="customer-current-routes" style={{ fontSize: '0.85rem', marginBottom: 4 }}>
                        สายปัจจุบันของลูกค้า: {currentRoutes.length ? currentRoutes.map(routeName).join(', ') : 'ยังไม่มีสาย'}
                    </div>
                    <select aria-label="ตั้งสายของลูกค้า" value={addRouteUid} onChange={(e) => setAddRouteUid(e.target.value)} style={inputStyle} disabled={tankCreated}>
                        <option value="">ไม่เปลี่ยนสาย</option>
                        {routeList.filter((r) => !currentRoutes.includes(r.uid)).map((r) => (
                            <option key={r.uid} value={r.uid}>{r.nameuser || r.username}</option>
                        ))}
                    </select>
                </div>
            )}
            <div style={{ marginBottom: 12 }}>
                <div style={labelStyle}>วันที่วางถัง</div>
                <input type="date" aria-label="วันที่วางถัง" value={form.datePlaced} onChange={set('datePlaced')} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
                <div style={labelStyle}>หมายเหตุ {!form.customerCode && <span style={{ color: '#E60012' }}>(จำเป็นถ้าไม่เลือกลูกค้า)</span>}</div>
                <textarea aria-label="หมายเหตุ" value={form.note} onChange={set('note')} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
            {error && <p className="error" role="alert" style={{ marginBottom: 12 }}>{error}</p>}
            <Footer onCancel={onClose} onSubmit={submit} busy={busy} submitLabel="สร้างถัง" />
        </Modal>
    );
};

export const MoveTankDialog = ({ tank, onClose, onDone }) => {
    const directory = useCustomerDirectory();
    const [to, setTo] = useState('');
    const [date, setDate] = useState(today());
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        if (!to) { setError('ต้องเลือกลูกค้าปลายทาง'); return; }
        setBusy(true);
        setError('');
        const res = await tankApi(`/${encodeURIComponent(tank.tankId)}/move`, {
            method: 'POST', body: { toCustomerCode: to, date, reason: reason || null },
        });
        setBusy(false);
        if (!res.ok) { setError(res.message); return; }
        onDone();
    };

    return (
        <Modal title={`ย้ายถัง ${tank.tankId}`} onClose={onClose}>
            <div style={{ marginBottom: 12, fontSize: '0.9rem' }}>
                ตอนนี้อยู่กับ: <strong>{tank.customerCode ? <CustomerLink name={directory.nameOf(tank.customerCode, tank.customerName)} code={tank.customerCode} /> : 'ไม่มีลูกค้า'}</strong>
            </div>
            <div style={{ marginBottom: 12 }}>
                <div style={labelStyle}>ย้ายไปร้าน *</div>
                <CustomerPicker value={to} onChange={setTo} directory={directory} />
            </div>
            <div style={{ marginBottom: 12 }}>
                <div style={labelStyle}>วันที่ย้าย</div>
                <input type="date" aria-label="วันที่ย้าย" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
                <div style={labelStyle}>เหตุผล / หมายเหตุ</div>
                <textarea aria-label="เหตุผล" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
            {error && <p className="error" role="alert" style={{ marginBottom: 12 }}>{error}</p>}
            <Footer onCancel={onClose} onSubmit={submit} busy={busy} submitLabel="ย้ายถัง" />
        </Modal>
    );
};

export const ReturnTankDialog = ({ tank, onClose, onDone }) => {
    const [date, setDate] = useState(today());
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        setBusy(true);
        setError('');
        const res = await tankApi(`/${encodeURIComponent(tank.tankId)}/return`, {
            method: 'POST', body: { date, reason: reason || null },
        });
        setBusy(false);
        if (!res.ok) { setError(res.message); return; }
        onDone();
    };

    return (
        <Modal title={`ถอนคืนโรงงาน ${tank.tankId}`} onClose={onClose}>
            <div style={{ marginBottom: 12, fontSize: '0.9rem' }}>
                {tank.customerCode ? <>ถอนถังออกจาก <strong><CustomerLink name={tank.customerName} code={tank.customerCode} /></strong></> : <>รับถังที่ให้ยืมคืน</>} — สถานะจะเป็น "คืนแล้ว" และไม่ผูกกับร้านใดอีก
            </div>
            <div style={{ marginBottom: 12 }}>
                <div style={labelStyle}>วันที่ถอน</div>
                <input type="date" aria-label="วันที่ถอน" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
                <div style={labelStyle}>เหตุผล / หมายเหตุ</div>
                <textarea aria-label="เหตุผล" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
            {error && <p className="error" role="alert" style={{ marginBottom: 12 }}>{error}</p>}
            <Footer onCancel={onClose} onSubmit={submit} busy={busy} submitLabel="ถอนคืนโรงงาน" />
        </Modal>
    );
};

// Deletes exactly ONE tank row, addressed by its row id (not the tank number) so a duplicated number
// removes the copy the user is looking at. The dialog repeats number / ปีรหัส / shop / status so the
// right copy is confirmed by eye. Movement history rows are keyed by number and are kept.
export const DeleteTankDialog = ({ tank, copies = 1, onClose, onDone }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const year = codeYear(tank);

    const submit = async () => {
        setBusy(true);
        setError('');
        const res = await tankApi(`/by-row/${encodeURIComponent(tank.id)}`, { method: 'DELETE' });
        setBusy(false);
        if (!res.ok) { setError(res.message); return; }
        onDone();
    };

    const row = (label, value) => (
        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <div style={{ width: 90, color: '#6B6B6B' }}>{label}</div>
            <div style={{ fontWeight: 600 }}>{value}</div>
        </div>
    );

    return (
        <Modal title={`ลบถัง ${tank.tankId}`} onClose={onClose}>
            <div style={{ marginBottom: 12, fontSize: '0.9rem' }}>
                {row('เลขถัง', tank.tankId)}
                {row('ปีรหัส', year.text || '-')}
                {row('ลูกค้า', tank.customerCode ? <CustomerLink name={tank.customerName} code={tank.customerCode} /> : 'ไม่มีลูกค้า')}
                {row('สถานะ', STATUS_LABELS[tank.status] || tank.status || '-')}
            </div>
            <div style={{ marginBottom: 12, fontSize: '0.85rem', color: '#6B6B6B' }}>
                ลบเฉพาะใบนี้ใบเดียว ลบแล้วกู้คืนไม่ได้
                {copies > 1 && <> · เลขนี้มีอีก {copies - 1} ใบ ซึ่งจะไม่ถูกลบ</>}
                {' '}· ประวัติการเคลื่อนไหวของเลขนี้ยังอยู่ · ระบบจะบันทึกว่าคุณเป็นคนลบ
            </div>
            {error && <p className="error" role="alert" style={{ marginBottom: 12 }}>{error}</p>}
            <Footer onCancel={onClose} onSubmit={submit} busy={busy} submitLabel="ลบถังใบนี้" />
        </Modal>
    );
};

const formatDeletedAt = (iso) => (iso ? String(iso).replace('T', ' ').slice(0, 16) : '-');

export const TankHistoryDialog = ({ tank, onClose }) => {
    const [rows, setRows] = useState(null);
    const [deletions, setDeletions] = useState([]);
    const [error, setError] = useState('');
    const [directory, setDirectory] = useState({});
    const customers = useCustomerDirectory();

    useEffect(() => {
        let cancelled = false;
        loadUserDirectory().then((map) => { if (!cancelled) setDirectory(map); });
        tankApi(`/${encodeURIComponent(tank.tankId)}/history`).then((res) => {
            if (cancelled) return;
            if (res.ok) setRows(res.data || []); else setError(res.message);
        });
        // Who deleted a copy of this number (log is keyed by row, snapshotted at delete time).
        tankApi(`/${encodeURIComponent(tank.tankId)}/deletions`).then((res) => {
            if (!cancelled && res.ok) setDeletions(res.data || []);
        });
        return () => { cancelled = true; };
    }, [tank.tankId]);

    const who = (code, name) => (code ? <CustomerLink name={customers.nameOf(code, name)} code={code} /> : '-');

    return (
        <Modal title={`ประวัติถัง ${tank.tankId}`} onClose={onClose} width={760}>
            {error && <p className="error">{error}</p>}
            {!rows && !error && <p>กำลังโหลด...</p>}
            {rows && (rows.length === 0 ? <p>ยังไม่มีประวัติ</p> : (
                <table className="btc-table">
                    <thead>
                        <tr><th>วันที่</th><th>รายการ</th><th>จาก</th><th>ไป</th><th>สถานะ</th><th>เหตุผล</th><th>ผู้ทำ</th></tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id}>
                                <td>{r.movementDate || '-'}</td>
                                <td>{MOVEMENT_LABELS[r.movementType] || r.movementType}</td>
                                <td>{who(r.fromCustomerCode, r.fromCustomerName)}</td>
                                <td>{who(r.toCustomerCode, r.toCustomerName)}</td>
                                <td>{r.fromStatus ? `${STATUS_LABELS[r.fromStatus] || r.fromStatus} → ` : ''}{STATUS_LABELS[r.toStatus] || r.toStatus || '-'}</td>
                                <td>{r.reason || '-'}</td>
                                <td><ActorName uid={r.createdBy} directory={directory} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ))}
            {deletions.length > 0 && (
                <div style={{ marginTop: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>บันทึกการลบถังเลขนี้</div>
                    <table className="btc-table">
                        <thead>
                            <tr><th>ลบเมื่อ</th><th>ปีรหัส</th><th>ลูกค้าตอนลบ</th><th>สถานะตอนลบ</th><th>ผู้ลบ</th></tr>
                        </thead>
                        <tbody>
                            {deletions.map((d) => (
                                <tr key={d.id}>
                                    <td>{formatDeletedAt(d.deletedAt)}</td>
                                    <td>{d.codeGeneration || '-'}</td>
                                    <td>{who(d.customerCode, d.customerName)}</td>
                                    <td>{STATUS_LABELS[d.status] || d.status || '-'}</td>
                                    <td><ActorName uid={d.deletedBy} directory={directory} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <Footer onCancel={onClose} busy={false} cancelLabel="ปิด" />
        </Modal>
    );
};
